import { createFileRoute, Link, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { useState, useMemo, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, Download, FileSpreadsheet, FileText, Loader2, Printer, Users, Hourglass, Trophy, Euro, XCircle, Clock, AlertTriangle, X, ExternalLink, UserCircle2, SlidersHorizontal, MoreVertical, Activity } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

import { crmGetMyAccess, crmListCities, crmReport, crmReportUsersAndLogs } from "@/lib/crm.functions";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/crm/reports")({
  component: ReportsPage,
});

function slug(s: string) {
  return s
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function download(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

const PIPELINE_STAGES = [
  { key: "prospekti", label: "Prospect", color: "#64748b", bg: "#f1f5f9" },
  { key: "kontaktoitu", label: "Contacted", color: "#0ea5e9", bg: "#e0f2fe" },
  { key: "tapaaminen", label: "Meeting", color: "#a855f7", bg: "#f3e8ff" },
  { key: "tarjous", label: "Proposal", color: "#f59e0b", bg: "#fef3c7" },
  { key: "kauppa", label: "Deal", color: "#10b981", bg: "#d1fae5" },
] as const;
type StageKey = typeof PIPELINE_STAGES[number]["key"];
function stageOf(c: any): StageKey {
  const s = c?.pipeline_stage as StageKey | null | undefined;
  if (s && PIPELINE_STAGES.some((p) => p.key === s)) return s;
  if (c?.status === "active") return "kauppa";
  return "prospekti";
}

function ReportsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const getMe = useServerFn(crmGetMyAccess);
  const listCities = useServerFn(crmListCities);
  const report = useServerFn(crmReport);

  useEffect(() => {
    const channel = supabase
      .channel("crm-reports-realtime")
      .on("postgres_changes", { event: "*", schema: "public", table: "crm_clients" }, () => {
        queryClient.invalidateQueries({ queryKey: ["crm", "report"] });
        queryClient.invalidateQueries({ queryKey: ["crm", "users-and-logs"] });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "crm_deals" }, () => {
        queryClient.invalidateQueries({ queryKey: ["crm", "report"] });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "crm_contracts" }, () => {
        queryClient.invalidateQueries({ queryKey: ["crm", "report"] });
      })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);


  const meQ = useQuery({ queryKey: ["crm", "me"], queryFn: () => getMe() });
  const citiesQ = useQuery({ queryKey: ["crm", "cities"], queryFn: () => listCities() });
  const [cityId, setCityId] = useState<string>("");
  const [segment, setSegment] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selected, setSelected] = useState<any | null>(null);
  const [category, setCategory] = useState<string>("");
  const [stageFilter, setStageFilter] = useState<StageKey | "">("");
  const [logsOpen, setLogsOpen] = useState(false);


  const reportQ = useQuery({
    queryKey: ["crm", "report", { cityId, segment }],
    queryFn: () =>
      report({
        data: {
          city_id: cityId || null,
          segment: (segment as any) || null,
        },
      }),
    enabled: meQ.data?.is_admin === true,
  });

  const fetchUL = useServerFn(crmReportUsersAndLogs);
  const ulQ = useQuery({
    queryKey: ["crm", "users-and-logs"],
    queryFn: () => fetchUL(),
    enabled: meQ.data?.is_admin === true,
    staleTime: 60_000,
  });

  const categoryClientIds = useMemo<Set<string> | null>(() => {
    if (!category || category === "clients") return null;
    const deals: any[] = reportQ.data?.deals ?? [];
    const contracts: any[] = reportQ.data?.contracts ?? [];
    const now = Date.now();
    const ids = new Set<string>();
    if (category === "pending" || category === "won" || category === "wonValue" || category === "lost") {
      const dealStatus = category === "wonValue" ? "won" : category;
      deals.forEach((d) => { if (d.status === dealStatus && d.client_id) ids.add(d.client_id); });
    } else if (category === "expiringSoon") {
      contracts.forEach((c) => {
        if (!c.ends_at || !c.client_id) return;
        const days = (new Date(c.ends_at).getTime() - now) / 86_400_000;
        if (days > 0 && days <= 30) ids.add(c.client_id);
      });
    } else if (category === "expired") {
      contracts.forEach((c) => {
        if (!c.ends_at || !c.client_id) return;
        if (new Date(c.ends_at).getTime() < now) ids.add(c.client_id);
      });
    }
    return ids;
  }, [category, reportQ.data]);

  const filteredClients = useMemo(() => {
    const all: any[] = reportQ.data?.clients ?? [];
    const fromMs = dateFrom ? new Date(dateFrom + "T00:00:00").getTime() : null;
    const toMs = dateTo ? new Date(dateTo + "T23:59:59.999").getTime() : null;
    return all.filter((c) => {
      if (categoryClientIds && !categoryClientIds.has(c.id)) return false;
      if (status && c.status !== status) return false;
      if (stageFilter && stageOf(c) !== stageFilter) return false;
      const t = new Date(c.updated_at).getTime();
      if (fromMs !== null && t < fromMs) return false;
      if (toMs !== null && t > toMs) return false;
      return true;
    });
  }, [reportQ.data, status, dateFrom, dateTo, categoryClientIds, stageFilter]);


  const activeFilterCount =
    (status ? 1 : 0) + (dateFrom ? 1 : 0) + (dateTo ? 1 : 0);

  const summary = useMemo(() => {
    if (!reportQ.data) return null;
    const { clients, deals, contracts } = reportQ.data;
    const won = deals.filter((d: any) => d.status === "won");
    const wonValue = won.reduce((s: number, d: any) => s + Number(d.value_eur ?? 0), 0);
    const now = Date.now();
    const expiringSoon = contracts.filter((c: any) => {
      if (!c.ends_at) return false;
      const days = (new Date(c.ends_at).getTime() - now) / 86_400_000;
      return days > 0 && days <= 30;
    }).length;
    const expired = contracts.filter(
      (c: any) => c.ends_at && new Date(c.ends_at).getTime() < now,
    ).length;
    return {
      clientCount: clients.length,
      pending: deals.filter((d: any) => d.status === "pending").length,
      won: won.length,
      lost: deals.filter((d: any) => d.status === "lost").length,
      wonValue,
      expiringSoon,
      expired,
    };
  }, [reportQ.data]);

  const cityName =
    (citiesQ.data?.cities ?? []).find((c: any) => c.id === cityId)?.name ?? "All cities";
  const segmentLabel =
    segment === "eat_drink"
      ? "Eat & Drink"
      : segment === "do"
        ? "Do"
        : segment === "happenings"
          ? "Happenings"
          : "All segments";
  const filterSuffix = `${slug(cityName)}-${slug(segmentLabel)}`;
  const today = new Date().toISOString().slice(0, 10);

  const exportCsv = () => {
    if (!reportQ.data) return;
    const reps: Record<string, { email: string | null; display_name: string | null }> =
      (reportQ.data as any)?.reps ?? {};
    const cityMap: Record<string, string> = (reportQ.data as any)?.cityMap ?? {};
    const repName = (id?: string | null) => {
      if (!id) return "";
      const r = reps[id];
      if (!r) return id.slice(0, 8);
      return r.display_name || r.email || id.slice(0, 8);
    };
    const segLbl = (s: string) =>
      s === "eat_drink" ? "Eat & Drink" : s === "happenings" ? "Happenings" : s === "do" ? "To Do" : (s ?? "");
    const rows = [
      ["id", "name", "business_id", "segment", "status", "city", "email", "phone", "website", "address", "added_by", "assigned_to", "notes", "created_at", "updated_at"],
      ...reportQ.data.clients.map((c: any) => [
        c.id,
        c.name,
        c.business_id ?? "",
        segLbl(c.segment),
        c.status,
        (c.city_id && cityMap[c.city_id]) || "",
        c.email ?? "",
        c.phone ?? "",
        c.website ?? "",
        c.address ?? "",
        repName(c.created_by),
        repName(c.assigned_rep),
        c.notes ?? "",
        c.created_at ?? "",
        c.updated_at ?? "",
      ]),
    ];
    const csv = rows
      .map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    download(new Blob([csv], { type: "text/csv" }), `crm-${filterSuffix}-${today}.csv`);
  };

  const exportXlsx = async () => {
    if (!reportQ.data || !summary) return;
    const XLSX = await import("xlsx");
    const wb = XLSX.utils.book_new();
    const meta = XLSX.utils.aoa_to_sheet([
      ["CRM Report"],
      ["Generated", today],
      ["City", cityName],
      ["Segment", segmentLabel],
      [],
      ["Metric", "Value"],
      ["Clients", summary.clientCount],
      ["Pending deals", summary.pending],
      ["Won", summary.won],
      ["Won value (EUR)", summary.wonValue],
      ["Lost", summary.lost],
      ["Expiring 30d", summary.expiringSoon],
      ["Expired", summary.expired],
    ]);
    XLSX.utils.book_append_sheet(wb, meta, "Summary");
    const reps2: Record<string, { email: string | null; display_name: string | null }> =
      (reportQ.data as any)?.reps ?? {};
    const cityMap2: Record<string, string> = (reportQ.data as any)?.cityMap ?? {};
    const repName = (id?: string | null) => {
      if (!id) return "";
      const r = reps2[id];
      if (!r) return id.slice(0, 8);
      return r.display_name || r.email || id.slice(0, 8);
    };
    const segLbl = (s: string) =>
      s === "eat_drink" ? "Eat & Drink" : s === "happenings" ? "Happenings" : s === "do" ? "To Do" : (s ?? "");
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(
        reportQ.data.clients.map((c: any) => ({
          name: c.name,
          business_id: c.business_id ?? "",
          segment: segLbl(c.segment),
          status: c.status,
          city: (c.city_id && cityMap2[c.city_id]) || "",
          email: c.email ?? "",
          phone: c.phone ?? "",
          website: c.website ?? "",
          address: c.address ?? "",
          added_by: repName(c.created_by),
          assigned_to: repName(c.assigned_rep),
          notes: c.notes ?? "",
          created_at: c.created_at,
          updated_at: c.updated_at,
        })),
      ),
      "Clients",
    );

    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(reportQ.data.deals ?? []), "Deals");
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(reportQ.data.contracts ?? []),
      "Contracts",
    );
    XLSX.writeFile(wb, `crm-${filterSuffix}-${today}.xlsx`);
  };

  const exportPdf = async (onlyClient?: any) => {
    if (!reportQ.data || !summary) return;

    const [{ default: jsPDF }, { default: autoTable }, ul, { WHAT_LOGO_WHITE_PNG_B64 }] = await Promise.all([
      import("jspdf"),
      import("jspdf-autotable"),
      fetchUsersAndLogs(),
      import("@/lib/brand-logo"),
    ]);
    const doc = new jsPDF();
    const pageW = doc.internal.pageSize.getWidth();
    const headerH = 18;
    const drawHeader = () => {
      doc.setFillColor(13, 27, 61);
      doc.rect(0, 0, pageW, headerH, "F");
      try {
        const lh = 10;
        const lw = (512 / 224) * lh;
        doc.addImage(`data:image/png;base64,${WHAT_LOGO_WHITE_PNG_B64}`, "PNG", 8, (headerH - lh) / 2, lw, lh, undefined, "FAST");
      } catch {}
      doc.setTextColor(255);
      doc.setFontSize(9);
      doc.text("whatfinland.com", pageW - 8, headerH / 2 + 1.5, { align: "right" });
      doc.setTextColor(0);
    };
    drawHeader();

    const reps: Record<string, { email: string | null; display_name: string | null }> =
      (reportQ.data as any)?.reps ?? {};
    const cityMap: Record<string, string> = (reportQ.data as any)?.cityMap ?? {};
    const repLabelLocal = (id?: string | null) => {
      if (!id) return "—";
      const r = reps[id];
      if (!r) return id.slice(0, 8);
      return r.display_name || r.email || id.slice(0, 8);
    };
    const segLabelOf = (s: string) =>
      s === "eat_drink" ? "Eat & Drink" : s === "happenings" ? "Happenings" : s === "do" ? "To Do" : (s ?? "—");
    const kindLabel = (k: string) =>
      k === "video" ? "Video Call" : k === "call" ? "Phone Call" : k === "in_person" ? "In Person" : (k ?? "—");

    // Group logs & meetings by client_id
    const logsByClient = new Map<string, any[]>();
    for (const l of (ul.logs ?? []) as any[]) {
      if (!l.client_id) continue;
      if (!logsByClient.has(l.client_id)) logsByClient.set(l.client_id, []);
      logsByClient.get(l.client_id)!.push(l);
    }
    const meetingsByClient = new Map<string, any[]>();
    for (const m of (ul.meetings ?? []) as any[]) {
      if (!m.client_id) continue;
      if (!meetingsByClient.has(m.client_id)) meetingsByClient.set(m.client_id, []);
      meetingsByClient.get(m.client_id)!.push(m);
    }

    // Deals & contracts grouped by client
    const dealsByClient = new Map<string, any[]>();
    for (const d of ((reportQ.data as any)?.deals ?? []) as any[]) {
      if (!d.client_id) continue;
      if (!dealsByClient.has(d.client_id)) dealsByClient.set(d.client_id, []);
      dealsByClient.get(d.client_id)!.push(d);
    }
    const contractsByClient = new Map<string, any[]>();
    for (const c of ((reportQ.data as any)?.contracts ?? []) as any[]) {
      if (!c.client_id) continue;
      if (!contractsByClient.has(c.client_id)) contractsByClient.set(c.client_id, []);
      contractsByClient.get(c.client_id)!.push(c);
    }

    const clientsToExport = onlyClient ? [onlyClient] : filteredClients;

    if (!onlyClient) {
      // Cover page: summary (skipped for single-client export)
      doc.setFontSize(16);
      doc.text("CRM Report", 14, headerH + 10);
      doc.setFontSize(10);
      doc.text(`Generated ${today}  ·  ${cityName}  ·  ${segmentLabel}`, 14, headerH + 16);
      autoTable(doc, {
        startY: headerH + 22,
        margin: { top: headerH + 4 },
        didDrawPage: () => drawHeader(),
        head: [["Metric", "Value"]],
        body: [
          ["Clients", String(summary.clientCount)],
          ["Pending deals", String(summary.pending)],
          ["Won", String(summary.won)],
          ["Won value (EUR)", summary.wonValue.toLocaleString()],
          ["Lost", String(summary.lost)],
          ["Expiring 30d", String(summary.expiringSoon)],
          ["Expired", String(summary.expired)],
        ],
      });
    }

    // One full profile per client, new page each — info + logs + meetings + deals
    clientsToExport.forEach((c: any, i: number) => {
      if (onlyClient && i === 0) {
        drawHeader();
      } else {
        doc.addPage();
        drawHeader();
      }
      doc.setFontSize(13);
      doc.text(String(c.name || "—"), 14, headerH + 10);
      doc.setFontSize(9);
      doc.setTextColor(110);
      if (!onlyClient) {
        doc.text(`Client ${i + 1} of ${clientsToExport.length}`, pageW - 14, headerH + 10, { align: "right" });
      }
      doc.setTextColor(0);




      const website = c.website
        ? (c.website.startsWith("http") ? c.website : `https://${c.website}`)
        : "—";
      autoTable(doc, {
        startY: headerH + 16,
        margin: { top: headerH + 4 },
        didDrawPage: () => drawHeader(),

        head: [["Client Info", ""]],
        body: [
          ["Business ID", c.business_id || "—"],
          ["Segment", segLabelOf(c.segment)],
          ["Status", c.status || "—"],
          ["City", (c.city_id && cityMap[c.city_id]) || "—"],
          ["Email", c.email || "—"],
          ["Phone", c.phone || "—"],
          ["Website", website],
          ["Address", c.address || "—"],
          ["Added By", repLabelLocal(c.created_by)],
          ["Assigned To", repLabelLocal(c.assigned_rep)],
          ["Created", c.created_at ? new Date(c.created_at).toLocaleString() : "—"],
          ["Updated", c.updated_at ? new Date(c.updated_at).toLocaleString() : "—"],
          ["Notes", c.notes || "—"],
        ],
        headStyles: { fillColor: [13, 110, 253] },
        styles: { cellWidth: "wrap", overflow: "linebreak", fontSize: 9 },
        columnStyles: { 0: { cellWidth: 40, fontStyle: "bold" }, 1: { cellWidth: 140 } },
      });

      // Deals
      const cDeals = dealsByClient.get(c.id) ?? [];
      autoTable(doc, {
        startY: (doc as any).lastAutoTable.finalY + 4,
        margin: { top: headerH + 4 },
        didDrawPage: () => drawHeader(),
        head: [[`Deals (${cDeals.length})`, "Status", "Value (EUR)", "Created", "Won/Lost"]],

        body: cDeals.length
          ? cDeals.map((d) => [
              d.title || d.id?.slice(0, 8) || "—",
              d.status || "—",
              d.value_eur != null ? Number(d.value_eur).toLocaleString() : "—",
              d.created_at ? new Date(d.created_at).toLocaleDateString() : "—",
              d.won_at ? new Date(d.won_at).toLocaleDateString() : d.lost_at ? new Date(d.lost_at).toLocaleDateString() : "—",
            ])
          : [["No deals", "", "", "", ""]],
        headStyles: { fillColor: [16, 185, 129] },
        styles: { fontSize: 8, cellWidth: "wrap", overflow: "linebreak" },
      });

      // Contracts
      const cContracts = contractsByClient.get(c.id) ?? [];
      autoTable(doc, {
        startY: (doc as any).lastAutoTable.finalY + 4,
        margin: { top: headerH + 4 },
        didDrawPage: () => drawHeader(),
        head: [[`Contracts (${cContracts.length})`, "Signed", "Ends"]],

        body: cContracts.length
          ? cContracts.map((k) => [
              k.title || k.id?.slice(0, 8) || "—",
              k.signed_at ? new Date(k.signed_at).toLocaleDateString() : "—",
              k.ends_at ? new Date(k.ends_at).toLocaleDateString() : "—",
            ])
          : [["No contracts", "", ""]],
        headStyles: { fillColor: [168, 85, 247] },
        styles: { fontSize: 8, cellWidth: "wrap", overflow: "linebreak" },
      });

      // Meetings
      const cMtgs = meetingsByClient.get(c.id) ?? [];
      autoTable(doc, {
        startY: (doc as any).lastAutoTable.finalY + 4,
        margin: { top: headerH + 4 },
        didDrawPage: () => drawHeader(),
        head: [[`Meetings (${cMtgs.length})`, "Type", "When", "Scheduled By", "Notes"]],

        body: cMtgs.length
          ? cMtgs.map((m) => [
              m.meeting_id || m.id.slice(0, 8),
              kindLabel(m.kind),
              m.scheduled_at ? new Date(m.scheduled_at).toLocaleString() : "—",
              m.scheduled_by_name || "—",
              m.notes || "—",
            ])
          : [["No meetings scheduled", "", "", "", ""]],
        headStyles: { fillColor: [245, 158, 11] },
        styles: { fontSize: 8, cellWidth: "wrap", overflow: "linebreak" },
        columnStyles: { 4: { cellWidth: 60 } },
      });

      // Activity logs (calls/emails/visits/whatsapp/etc.)
      const cLogs = logsByClient.get(c.id) ?? [];
      autoTable(doc, {
        startY: (doc as any).lastAutoTable.finalY + 4,
        margin: { top: headerH + 4 },
        didDrawPage: () => drawHeader(),
        head: [[`Activity Logs (${cLogs.length})`, "Channel", "Actor", "Summary"]],

        body: cLogs.length
          ? cLogs.map((l) => [
              l.occurred_at ? new Date(l.occurred_at).toLocaleString() : "—",
              channelLabel(l.channel),
              l.actor_name || "—",
              l.summary || "—",
            ])
          : [["No activity recorded", "", "", ""]],
        headStyles: { fillColor: [59, 130, 246] },
        styles: { fontSize: 8, cellWidth: "wrap", overflow: "linebreak" },
        columnStyles: { 0: { cellWidth: 36 }, 1: { cellWidth: 22 }, 2: { cellWidth: 30 }, 3: { cellWidth: "auto" } },
      });
    });

    const fileSuffix = onlyClient
      ? `client-${slug(String(onlyClient.name || onlyClient.id))}`
      : filterSuffix;
    doc.save(`crm-${fileSuffix}-${today}.pdf`);

  };

  const fetchUsersAndLogs = useServerFn(crmReportUsersAndLogs);
  const loadUL = async () => await fetchUsersAndLogs();

  const channelLabel = (c: string) =>
    c === "call" ? "Call" : c === "email" ? "Email" : c === "visit" ? "Visit / Meeting" : c === "whatsapp" ? "WhatsApp" : c === "other" ? "Other" : (c ?? "");

  const exportUsersXlsx = async () => {
    const { users } = await loadUL();
    const XLSX = await import("xlsx");
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(
        users.map((u: any) => ({
          user_id: u.user_id,
          email: u.email ?? "",
          display_name: u.display_name ?? "",
          roles: (u.roles ?? []).join(", "),
          is_admin: u.is_admin ? "yes" : "",
          is_super_admin: u.is_super_admin ? "yes" : "",
          is_sales_rep: u.is_sales_rep ? "yes" : "",
          disabled: u.banned_until && new Date(u.banned_until).getTime() > Date.now() ? "yes" : "",
          created_at: u.created_at ?? "",
          last_sign_in_at: u.last_sign_in_at ?? "",
        })),
      ),
      "Users",
    );
    XLSX.writeFile(wb, `crm-users-${today}.xlsx`);
  };

  const exportUsersPdf = async () => {
    const { users } = await loadUL();
    const [{ default: jsPDF }, { default: autoTable }, { WHAT_LOGO_WHITE_PNG_B64 }] = await Promise.all([
      import("jspdf"),
      import("jspdf-autotable"),
      import("@/lib/brand-logo"),
    ]);
    const doc = new jsPDF();
    const pageW = doc.internal.pageSize.getWidth();
    const headerH = 18;
    const drawHeader = () => {
      doc.setFillColor(13, 27, 61);
      doc.rect(0, 0, pageW, headerH, "F");
      try {
        const lh = 10;
        const lw = (512 / 224) * lh;
        doc.addImage(`data:image/png;base64,${WHAT_LOGO_WHITE_PNG_B64}`, "PNG", 8, (headerH - lh) / 2, lw, lh, undefined, "FAST");
      } catch {}
      doc.setTextColor(255);
      doc.setFontSize(9);
      doc.text("whatfinland.com", pageW - 8, headerH / 2 + 1.5, { align: "right" });
      doc.setTextColor(0);
    };
    drawHeader();
    doc.setFontSize(16);
    doc.text("CRM Users", 14, headerH + 10);
    doc.setFontSize(10);
    doc.text(`Generated ${today}  ·  ${users.length} users`, 14, headerH + 16);
    users.forEach((u: any, i: number) => {
      if (i > 0) {
        doc.addPage();
        drawHeader();
      }
      const yStart = i === 0 ? headerH + 24 : headerH + 10;
      doc.setFontSize(14);
      doc.text(String(u.display_name || u.email || "—"), 14, yStart);
      autoTable(doc, {
        startY: yStart + 6,
        margin: { top: headerH + 4 },
        didDrawPage: () => drawHeader(),
        head: [["Field", "Value"]],
        body: [
          ["User ID", u.user_id],
          ["Email", u.email ?? "—"],
          ["Display Name", u.display_name ?? "—"],
          ["Roles", (u.roles ?? []).join(", ") || "—"],
          ["Admin", u.is_admin ? "Yes" : "No"],
          ["Super Admin", u.is_super_admin ? "Yes" : "No"],
          ["Sales Rep", u.is_sales_rep ? "Yes" : "No"],
          ["Disabled", u.banned_until && new Date(u.banned_until).getTime() > Date.now() ? "Yes" : "No"],
          ["Created", u.created_at ? new Date(u.created_at).toLocaleString() : "—"],
          ["Last Sign-in", u.last_sign_in_at ? new Date(u.last_sign_in_at).toLocaleString() : "—"],
        ],
        styles: { cellWidth: "wrap", overflow: "linebreak" },
        columnStyles: { 0: { cellWidth: 45, fontStyle: "bold" }, 1: { cellWidth: 135 } },
      });
    });
    doc.save(`crm-users-${today}.pdf`);
  };


  const exportLogsXlsx = async () => {
    const { logs } = await loadUL();
    const XLSX = await import("xlsx");
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(
        logs.map((l: any) => ({
          occurred_at: l.occurred_at,
          channel: channelLabel(l.channel),
          client: l.client_name,
          actor: l.actor_name,
          summary: l.summary,
          client_id: l.client_id,
          actor_user_id: l.actor_user_id,
          created_at: l.created_at,
        })),
      ),
      "Activity Logs",
    );
    XLSX.writeFile(wb, `crm-logs-${today}.xlsx`);
  };

  const exportLogsPdf = async () => {
    const { logs } = await loadUL();
    const [{ default: jsPDF }, { default: autoTable }, { WHAT_LOGO_WHITE_PNG_B64 }] = await Promise.all([
      import("jspdf"),
      import("jspdf-autotable"),
      import("@/lib/brand-logo"),
    ]);
    const doc = new jsPDF({ orientation: "landscape" });
    const pageW = doc.internal.pageSize.getWidth();
    const headerH = 18;
    const drawHeader = () => {
      doc.setFillColor(13, 27, 61);
      doc.rect(0, 0, pageW, headerH, "F");
      try {
        const lh = 10;
        const lw = (512 / 224) * lh;
        doc.addImage(`data:image/png;base64,${WHAT_LOGO_WHITE_PNG_B64}`, "PNG", 8, (headerH - lh) / 2, lw, lh, undefined, "FAST");
      } catch {}
      doc.setTextColor(255);
      doc.setFontSize(9);
      doc.text("whatfinland.com", pageW - 8, headerH / 2 + 1.5, { align: "right" });
      doc.setTextColor(0);
    };
    drawHeader();
    doc.setFontSize(16);
    doc.text("CRM Activity Logs", 14, headerH + 10);
    doc.setFontSize(10);
    doc.text(`Generated ${today}  ·  ${logs.length} entries`, 14, headerH + 16);
    autoTable(doc, {
      startY: headerH + 22,
      margin: { top: headerH + 4 },
      didDrawPage: () => drawHeader(),
      head: [["When", "Channel", "Client", "Actor", "Summary"]],
      body: logs.map((l: any) => [
        l.occurred_at ? new Date(l.occurred_at).toLocaleString() : "—",
        channelLabel(l.channel),
        l.client_name || "—",
        l.actor_name || "—",
        l.summary || "—",
      ]),
      styles: { fontSize: 8, cellWidth: "wrap", overflow: "linebreak" },
      columnStyles: { 0: { cellWidth: 38 }, 1: { cellWidth: 25 }, 2: { cellWidth: 45 }, 3: { cellWidth: 40 }, 4: { cellWidth: "auto" } },
    });
    doc.save(`crm-logs-${today}.pdf`);
  };




  if (meQ.isLoading) {
    return (
      <div className="min-h-screen grid place-items-center">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (!meQ.data?.is_admin) {
    return (
      <div className="min-h-screen grid place-items-center p-6 text-center">
        <p>Admins only.</p>
      </div>
    );
  }

  const reps: Record<string, { email: string | null; display_name: string | null }> =
    (reportQ.data as any)?.reps ?? {};
  const cityMap: Record<string, string> = (reportQ.data as any)?.cityMap ?? {};
  const repLabel = (id?: string | null) => {
    if (!id) return "—";
    const r = reps[id];
    if (!r) return id.slice(0, 8);
    return r.display_name || r.email || id.slice(0, 8);
  };

  const isReportsIndex = location.pathname === "/crm/reports";

  return (
    <>
      {isReportsIndex ? (
        <div className="h-full overflow-y-auto bg-background pb-12 print:bg-white">
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #crm-print-area, #crm-print-area * { visibility: visible !important; }
          #crm-print-area { position: absolute !important; inset: 0 !important; width: 100% !important; padding: 16px !important; color: #000 !important; background: #fff !important; }
          #crm-print-area .border { border-color: #ddd !important; }
          #crm-print-area .bg-muted { background: #f3f4f6 !important; }
          #crm-print-area .text-muted-foreground { color: #555 !important; }
          /* Hide the on-screen table & mobile cards when printing — use the
             dedicated print-only profile layout instead. */
          #crm-print-area .crm-screen-only { display: none !important; }
          #crm-print-area .crm-print-only { display: block !important; }
          .crm-print-profile { page-break-after: always; break-after: page; }
          .crm-print-profile:last-child { page-break-after: auto; break-after: auto; }
        }
        .crm-print-only { display: none; }
      `}</style>

      <header className="sticky top-0 z-20 rounded-2xl border border-border bg-background/95 backdrop-blur print:hidden">
        <div className="mx-auto max-w-7xl grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 px-3 sm:px-4 lg:px-6 py-2.5 sm:py-3">
          <Link to="/crm" className="rounded-md p-2 hover:bg-muted" aria-label="Back">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <h1 className="truncate text-sm sm:text-base font-bold">CRM Reports</h1>
          <div className="flex gap-1 sm:gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={async () => { if (!ulQ.data) { await ulQ.refetch(); } setLogsOpen(true); }}
              className="h-9 w-9 p-0 sm:w-auto sm:px-3"
              aria-label="View all activity logs"
              title="View all activity logs"
            >
              <Activity className="h-4 w-4 sm:mr-1 sm:h-3 sm:w-3" /> <span className="hidden sm:inline">Logs</span>
            </Button>
            <Button variant="outline" size="sm" onClick={async () => { if (!ulQ.data) { await ulQ.refetch(); } setTimeout(() => window.print(), 50); }} className="h-9 w-9 p-0 sm:w-auto sm:px-3" aria-label="Print">
              <Printer className="h-4 w-4 sm:mr-1 sm:h-3 sm:w-3" /> <span className="hidden sm:inline">Print</span>
            </Button>
            <Button variant="outline" size="sm" onClick={() => exportPdf()} className="h-9 w-9 p-0 sm:w-auto sm:px-3" aria-label="Export PDF">
              <FileText className="h-4 w-4 sm:mr-1 sm:h-3 sm:w-3" /> <span className="hidden sm:inline">PDF</span>
            </Button>
            <Button size="sm" onClick={exportCsv} className="h-9 w-9 p-0 sm:w-auto sm:px-3" aria-label="Export CSV">
              <Download className="h-4 w-4 sm:mr-1 sm:h-3 sm:w-3" /> <span className="hidden sm:inline">CSV</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-9 w-9 p-0 sm:w-auto sm:px-3" aria-label="More exports">
                  <MoreVertical className="h-4 w-4 sm:mr-1 sm:h-3 sm:w-3" />
                  <span className="hidden sm:inline">More</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="flex items-center gap-2"><Users className="h-3.5 w-3.5" /> Full Users</DropdownMenuLabel>
                <DropdownMenuItem onClick={exportUsersPdf}>
                  <FileText className="h-4 w-4 mr-2" /> Users — PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={exportUsersXlsx}>
                  <FileSpreadsheet className="h-4 w-4 mr-2" /> Users — Excel
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="flex items-center gap-2"><Activity className="h-3.5 w-3.5" /> Activity Logs</DropdownMenuLabel>
                <DropdownMenuItem onClick={exportLogsPdf}>
                  <FileText className="h-4 w-4 mr-2" /> Logs — PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={exportLogsXlsx}>
                  <FileSpreadsheet className="h-4 w-4 mr-2" /> Logs — Excel
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

        </div>
        <div className="grid grid-cols-[1fr_1fr_auto] gap-2 px-3 pb-2.5 sm:pb-3 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,160px)_minmax(0,160px)_auto] lg:items-end">
          <Select value={cityId || "all"} onValueChange={(v) => setCityId(v === "all" ? "" : v)}>
            <SelectTrigger className="h-9">
              <SelectValue placeholder="City" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All cities</SelectItem>
              {(citiesQ.data?.cities ?? []).map((c: any) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={segment || "all"} onValueChange={(v) => setSegment(v === "all" ? "" : v)}>
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Segment" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All segments</SelectItem>
              <SelectItem value="eat_drink">Eat & Drink</SelectItem>
              <SelectItem value="do">Do</SelectItem>
              <SelectItem value="happenings">Happenings</SelectItem>
            </SelectContent>
          </Select>
          <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
            <SelectTrigger className="hidden lg:flex h-9">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="churned">Churned</SelectItem>
            </SelectContent>
          </Select>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            aria-label="From date"
            className="hidden lg:block h-9 w-full min-w-0 rounded-md border border-input bg-background px-2 text-xs"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            aria-label="To date"
            className="hidden lg:block h-9 w-full min-w-0 rounded-md border border-input bg-background px-2 text-xs"
          />
          <Button
            variant={filtersOpen || activeFilterCount > 0 ? "default" : "outline"}
            size="sm"
            onClick={() => {
              if (window.matchMedia("(min-width: 1024px)").matches && activeFilterCount > 0) {
                setStatus("");
                setDateFrom("");
                setDateTo("");
              } else {
                setFiltersOpen((v) => !v);
              }
            }}
            className="h-9 px-3 relative"
            aria-label="More filters"
          >
            <SlidersHorizontal className="h-4 w-4 sm:mr-1 lg:hidden" />
            <span className="hidden sm:inline lg:hidden">Filters</span>
            <span className="hidden lg:inline">Clear</span>
            {activeFilterCount > 0 && (
              <span className="ml-1 inline-flex items-center justify-center rounded-full bg-background/30 px-1.5 text-[10px] font-bold tabular-nums lg:hidden">
                {activeFilterCount}
              </span>
            )}
          </Button>
        </div>
        {filtersOpen && (
          <div className="grid grid-cols-2 gap-2 px-3 pb-2.5 sm:grid-cols-4 sm:pb-3 lg:hidden">
            <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
              <SelectTrigger className="col-span-2 h-9 sm:col-span-1">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="churned">Churned</SelectItem>
              </SelectContent>
            </Select>
            <label className="flex min-w-0 flex-col gap-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              <span className="px-0.5">From</span>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-9 w-full min-w-0 rounded-md border border-input bg-background px-2 text-xs"
              />
            </label>
            <label className="flex min-w-0 flex-col gap-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              <span className="px-0.5">To</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-9 w-full min-w-0 rounded-md border border-input bg-background px-2 text-xs"
              />
            </label>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setStatus("");
                setDateFrom("");
                setDateTo("");
              }}
              disabled={activeFilterCount === 0}
              className="col-span-2 h-9 self-end sm:col-span-1"
            >
              Clear
            </Button>
          </div>

        )}

      </header>

      <main id="crm-print-area" className="mx-auto max-w-7xl px-3 sm:px-4 lg:px-6 py-3 sm:py-4 lg:py-6 space-y-4">
        <div className="mb-4 hidden print:block">
          <h1 className="text-xl font-bold">CRM Report</h1>
          <p className="text-xs text-muted-foreground">{new Date().toLocaleString()}</p>
        </div>

        <Link
          to="/crm/reports/monthly"
          className="block rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white p-4 hover:shadow-sm transition print:hidden"
        >
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-emerald-900">
                Monthly Sales & Forecast →
              </div>
              <div className="text-xs text-emerald-800/70">
                Past 12 months of revenue and next 6 months of expected recurring income.
              </div>
            </div>
            <ExternalLink className="h-4 w-4 text-emerald-700" />
          </div>
        </Link>

        <Link
          to="/crm/reports/contacts"
          className="block rounded-xl border border-sky-200 bg-gradient-to-r from-sky-50 to-white p-4 hover:shadow-sm transition print:hidden"
        >
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-sky-900">
                Contacts Report →
              </div>
              <div className="text-xs text-sky-800/70">
                See contacts by day, yesterday / last week / last month, and by salesperson.
              </div>
            </div>
            <ExternalLink className="h-4 w-4 text-sky-700" />
          </div>
        </Link>

        {reportQ.isLoading ? (
          <div className="grid place-items-center py-12">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : !summary ? (
          <p className="text-sm text-muted-foreground">No data.</p>
        ) : (
          <>


            {(() => {
              const allClients: any[] = reportQ.data?.clients ?? [];
              const counts: Record<StageKey, number> = {
                prospekti: 0, kontaktoitu: 0, tapaaminen: 0, tarjous: 0, kauppa: 0,
              };
              for (const c of allClients) counts[stageOf(c)]++;
              return (
                <section className="mt-4 rounded-xl border border-border bg-white overflow-hidden">
                  <header className="px-4 py-2.5 border-b border-border bg-gradient-to-r from-slate-50 to-white flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Activity className="h-4 w-4 text-slate-600" />
                      <h2 className="text-sm font-bold">Pipeline by Stage</h2>
                    </div>
                    {stageFilter && (
                      <button
                        type="button"
                        onClick={() => setStageFilter("")}
                        className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                      >
                        <X className="h-3 w-3" /> Clear
                      </button>
                    )}
                  </header>
                  <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 p-3">
                    {PIPELINE_STAGES.map((s) => {
                      const active = stageFilter === s.key;
                      return (
                        <button
                          key={s.key}
                          type="button"
                          onClick={() => setStageFilter((prev) => (prev === s.key ? "" : s.key))}
                          className={`text-left rounded-lg px-3 py-2 ring-1 transition ${active ? "ring-2" : "ring-black/5 hover:ring-black/10"}`}
                          style={{ background: s.bg, color: s.color, boxShadow: active ? `inset 0 0 0 2px ${s.color}` : undefined }}
                        >
                          <div className="text-[10px] font-semibold uppercase tracking-wide opacity-80">{s.label}</div>
                          <div className="text-lg font-bold tabular-nums">{counts[s.key]}</div>
                        </button>
                      );
                    })}
                    {(() => {
                      const active = category === "expiringSoon";
                      const count = summary?.expiringSoon ?? 0;
                      return (
                        <button
                          type="button"
                          onClick={() => setCategory((prev) => (prev === "expiringSoon" ? "" : "expiringSoon"))}
                          className={`text-left rounded-lg px-3 py-2 ring-1 transition ${active ? "ring-2" : "ring-black/5 hover:ring-black/10"}`}
                          style={{ background: "#fee2e2", color: "#b91c1c", boxShadow: active ? `inset 0 0 0 2px #b91c1c` : undefined }}
                        >
                          <div className="text-[10px] font-semibold uppercase tracking-wide opacity-80">Expired 30d</div>
                          <div className="text-lg font-bold tabular-nums">{count}</div>
                        </button>
                      );
                    })()}
                  </div>

                </section>
              );
            })()}

            <h2 className="mt-6 mb-2 text-sm font-semibold">
              Clients <span className="text-muted-foreground font-normal">({filteredClients.length})</span>
              {stageFilter && (
                <span className="ml-2 text-[11px] font-normal text-muted-foreground">
                  · stage: {PIPELINE_STAGES.find((s) => s.key === stageFilter)?.label}
                </span>
              )}
            </h2>

            {/* Mobile: gradient card list */}
            <ul className="sm:hidden space-y-2.5 crm-screen-only">

              {filteredClients.map((c: any) => {
                const segTint =
                  c.segment === "eat_drink" ? "#f97316" :
                  c.segment === "happenings" ? "#a855f7" :
                  c.segment === "do" ? "#0ea5e9" : "#0d6efd";
                const segLabel =
                  c.segment === "eat_drink" ? "Eat & Drink" :
                  c.segment === "happenings" ? "Happenings" :
                  c.segment === "do" ? "To Do" : c.segment;
                const stTint =
                  c.status === "active" || c.status === "won" ? "#16a34a" :
                  c.status === "churned" || c.status === "lost" ? "#dc2626" :
                  c.status === "pending" ? "#f59e0b" : "#0d6efd";
                const initials = String(c.name || "?")
                  .split(/\s+/).filter(Boolean).slice(0, 2)
                  .map((w: string) => w[0]?.toUpperCase()).join("") || "?";
                return (
                  <li
                    key={c.id}
                    onClick={() => setSelected(c)}
                    className="relative overflow-hidden rounded-2xl p-3 ring-1 ring-black/5 active:scale-[0.99] transition-transform cursor-pointer"
                    style={{
                      background: `linear-gradient(135deg, ${segTint}18 0%, rgba(255,255,255,0.92) 55%, rgba(255,255,255,0.98) 100%)`,
                      boxShadow: `inset 0 1px 0 rgba(255,255,255,0.7), 0 1px 2px rgba(13,17,38,0.04), 0 10px 22px -16px ${segTint}66`,
                    }}
                  >
                    <span
                      aria-hidden
                      className="pointer-events-none absolute -top-8 -right-8 size-24 rounded-full blur-2xl opacity-60"
                      style={{ background: `radial-gradient(circle, ${segTint}55, transparent 70%)` }}
                    />
                    <span
                      aria-hidden
                      className="absolute inset-y-2 left-0 w-[3px] rounded-r-full"
                      style={{ background: `linear-gradient(180deg, ${segTint}, ${segTint}80)` }}
                    />
                    <div className="relative flex items-start gap-3">
                      <div
                        className="grid size-10 shrink-0 place-items-center rounded-xl text-sm font-bold text-white shadow-sm"
                        style={{ background: `linear-gradient(135deg, ${segTint}, ${segTint}cc)` }}
                      >
                        {initials}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="truncate text-sm font-bold tracking-tight">{c.name}</h3>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <span
                              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                              style={{ background: `${stTint}1f`, color: stTint }}
                            >
                              <span className="size-1.5 rounded-full" style={{ background: stTint }} />
                              {c.status}
                            </span>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); exportPdf(c); }}
                              className="grid size-7 place-items-center rounded-full bg-white/80 text-slate-700 ring-1 ring-black/5 active:scale-95"
                              aria-label={`Download PDF for ${c.name}`}
                            >
                              <Download className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>

                        <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                          <span
                            className="rounded-full px-2 py-0.5 text-[10px] font-medium"
                            style={{ background: `${segTint}1a`, color: segTint }}
                          >
                            {segLabel}
                          </span>
                          {(() => {
                            const st = PIPELINE_STAGES.find((p) => p.key === stageOf(c))!;
                            return (
                              <span
                                className="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                                style={{ background: st.bg, color: st.color }}
                              >
                                {st.label}
                              </span>
                            );
                          })()}
                          <span className="text-[10px] text-muted-foreground tabular-nums">
                            {new Date(c.updated_at).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="mt-1.5 flex items-center gap-1 text-[11px] text-muted-foreground min-w-0">
                          <UserCircle2 className="h-3 w-3 shrink-0" />
                          <span className="truncate">{repLabel(c.created_by)}</span>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>

            {/* Desktop: table */}
            <div className="hidden sm:block rounded-xl border border-border bg-card overflow-hidden shadow-[0_1px_0_rgba(13,17,38,0.04)] crm-screen-only">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-xs">
                  <thead className="bg-muted/60 text-[10px] uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold w-[20%]">Name</th>
                      <th className="px-3 py-2 text-left font-semibold w-[11%]">Segment</th>
                      <th className="px-3 py-2 text-left font-semibold w-[12%]">Stage</th>
                      <th className="px-3 py-2 text-left font-semibold w-[11%]">Status</th>
                      <th className="px-3 py-2 text-left font-semibold w-[15%]">Added By</th>
                      <th className="px-3 py-2 text-left font-semibold w-[15%]">Assigned</th>
                      <th className="px-3 py-2 text-right font-semibold w-[10%]">Updated</th>
                      <th className="px-3 py-2 text-right font-semibold w-[6%]">PDF</th>
                    </tr>

                  </thead>
                  <tbody>
                    {filteredClients.map((c: any) => (
                      <tr
                        key={c.id}
                        onClick={() => setSelected(c)}
                        className="border-t border-border cursor-pointer transition-colors hover:bg-muted/40"
                      >
                        <td className="px-3 py-2 font-semibold">
                          <div className="truncate max-w-[200px]">{c.name}</div>
                        </td>
                        <td className="px-3 py-2">
                          <span className="inline-block max-w-full truncate rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-medium text-indigo-700 align-middle">
                            {c.segment === "eat_drink" ? "Eat & Drink" : c.segment === "happenings" ? "Happenings" : c.segment === "do" ? "To Do" : c.segment}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {(() => {
                            const st = PIPELINE_STAGES.find((p) => p.key === stageOf(c))!;
                            return (
                              <span
                                className="inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold"
                                style={{ background: st.bg, color: st.color }}
                              >
                                {st.label}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className="inline-flex max-w-full items-center gap-1 truncate rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize align-middle"
                            style={{
                              background:
                                c.status === "active" ? "#19875422" :
                                c.status === "churned" ? "#dc354522" : "#0d6efd22",
                              color:
                                c.status === "active" ? "#198754" :
                                c.status === "churned" ? "#dc3545" : "#0d6efd",
                            }}
                          >
                            <span
                              className="size-1.5 shrink-0 rounded-full"
                              style={{
                                background:
                                  c.status === "active" ? "#198754" :
                                  c.status === "churned" ? "#dc3545" : "#0d6efd",
                              }}
                            />
                            {c.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">
                          <div className="truncate">{repLabel(c.created_by)}</div>
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">
                          <div className="truncate">{repLabel(c.assigned_rep)}</div>
                        </td>
                        <td className="px-3 py-2 text-right text-muted-foreground tabular-nums whitespace-nowrap">
                          {new Date(c.updated_at).toLocaleDateString()}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); exportPdf(c); }}
                            className="inline-grid size-7 place-items-center rounded-md border border-border bg-background text-muted-foreground hover:text-foreground hover:bg-muted"
                            aria-label={`Download PDF for ${c.name}`}
                            title="Download client PDF"
                          >
                            <Download className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}

                  </tbody>
                </table>
              </div>
            </div>

            {/* Print-only: one full profile per client */}
            <div className="crm-print-only space-y-6">
              {(() => {
                const channelLabelP = (c: string) =>
                  c === "call" ? "Call" : c === "email" ? "Email" : c === "visit" ? "Visit / Meeting" : c === "whatsapp" ? "WhatsApp" : c === "other" ? "Other" : (c ?? "");
                const kindLabelP = (k: string) =>
                  k === "video" ? "Video Call" : k === "call" ? "Phone Call" : k === "in_person" ? "In Person" : (k ?? "—");
                const logsByClient = new Map<string, any[]>();
                for (const l of ((ulQ.data?.logs ?? []) as any[])) {
                  if (!l.client_id) continue;
                  if (!logsByClient.has(l.client_id)) logsByClient.set(l.client_id, []);
                  logsByClient.get(l.client_id)!.push(l);
                }
                const meetingsByClient = new Map<string, any[]>();
                for (const m of ((ulQ.data?.meetings ?? []) as any[])) {
                  if (!m.client_id) continue;
                  if (!meetingsByClient.has(m.client_id)) meetingsByClient.set(m.client_id, []);
                  meetingsByClient.get(m.client_id)!.push(m);
                }
                const dealsByClient = new Map<string, any[]>();
                for (const d of (((reportQ.data as any)?.deals ?? []) as any[])) {
                  if (!d.client_id) continue;
                  if (!dealsByClient.has(d.client_id)) dealsByClient.set(d.client_id, []);
                  dealsByClient.get(d.client_id)!.push(d);
                }
                const contractsByClient = new Map<string, any[]>();
                for (const k of (((reportQ.data as any)?.contracts ?? []) as any[])) {
                  if (!k.client_id) continue;
                  if (!contractsByClient.has(k.client_id)) contractsByClient.set(k.client_id, []);
                  contractsByClient.get(k.client_id)!.push(k);
                }
                return filteredClients.map((c: any) => {
                  const segLabel =
                    c.segment === "eat_drink" ? "Eat & Drink" :
                    c.segment === "happenings" ? "Happenings" :
                    c.segment === "do" ? "To Do" : (c.segment ?? "—");
                  const cityName = (c.city_id && cityMap[c.city_id]) || "—";
                  const website = c.website
                    ? (c.website.startsWith("http") ? c.website : `https://${c.website}`)
                    : null;
                  const rows: Array<[string, string]> = [
                    ["Business ID", c.business_id || "—"],
                    ["Segment", segLabel],
                    ["Status", c.status || "—"],
                    ["City", cityName],
                    ["Email", c.email || "—"],
                    ["Phone", c.phone || "—"],
                    ["Website", website || "—"],
                    ["Address", c.address || "—"],
                    ["Added By", repLabel(c.created_by)],
                    ["Assigned To", repLabel(c.assigned_rep)],
                    ["Created", c.created_at ? new Date(c.created_at).toLocaleString() : "—"],
                    ["Updated", c.updated_at ? new Date(c.updated_at).toLocaleString() : "—"],
                  ];
                  const cDeals = dealsByClient.get(c.id) ?? [];
                  const cContracts = contractsByClient.get(c.id) ?? [];
                  const cMtgs = meetingsByClient.get(c.id) ?? [];
                  const cLogs = logsByClient.get(c.id) ?? [];
                  return (
                    <section
                      key={c.id}
                      className="crm-print-profile rounded-xl border border-border bg-white p-5"
                    >
                      <header className="mb-3 border-b pb-2">
                        <h2 className="text-lg font-bold">{c.name}</h2>
                        <p className="text-xs text-muted-foreground">
                          {segLabel} · {c.status || "—"} · {cityName}
                        </p>
                      </header>
                      <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
                        {rows.map(([k, v]) => (
                          <div key={k} className="flex gap-2">
                            <dt className="w-28 shrink-0 font-semibold text-muted-foreground">{k}</dt>
                            <dd className="min-w-0 break-words">{v}</dd>
                          </div>
                        ))}
                      </dl>
                      {c.notes && (
                        <div className="mt-3">
                          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Notes</p>
                          <p className="mt-1 whitespace-pre-wrap text-xs">{c.notes}</p>
                        </div>
                      )}

                      <div className="mt-4">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Deals ({cDeals.length})</p>
                        {cDeals.length ? (
                          <table className="w-full text-[11px] border border-border">
                            <thead className="bg-muted"><tr><th className="text-left px-2 py-1">Title</th><th className="text-left px-2 py-1">Status</th><th className="text-right px-2 py-1">Value (EUR)</th><th className="text-left px-2 py-1">Created</th><th className="text-left px-2 py-1">Won/Lost</th></tr></thead>
                            <tbody>
                              {cDeals.map((d: any) => (
                                <tr key={d.id} className="border-t border-border">
                                  <td className="px-2 py-1">{d.title || d.id?.slice(0, 8) || "—"}</td>
                                  <td className="px-2 py-1">{d.status || "—"}</td>
                                  <td className="px-2 py-1 text-right tabular-nums">{d.value_eur != null ? Number(d.value_eur).toLocaleString() : "—"}</td>
                                  <td className="px-2 py-1">{d.created_at ? new Date(d.created_at).toLocaleDateString() : "—"}</td>
                                  <td className="px-2 py-1">{d.won_at ? new Date(d.won_at).toLocaleDateString() : d.lost_at ? new Date(d.lost_at).toLocaleDateString() : "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : <p className="text-[11px] text-muted-foreground">No deals</p>}
                      </div>

                      <div className="mt-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Contracts ({cContracts.length})</p>
                        {cContracts.length ? (
                          <table className="w-full text-[11px] border border-border">
                            <thead className="bg-muted"><tr><th className="text-left px-2 py-1">Title</th><th className="text-left px-2 py-1">Signed</th><th className="text-left px-2 py-1">Ends</th></tr></thead>
                            <tbody>
                              {cContracts.map((k: any) => (
                                <tr key={k.id} className="border-t border-border">
                                  <td className="px-2 py-1">{k.title || k.id?.slice(0, 8) || "—"}</td>
                                  <td className="px-2 py-1">{k.signed_at ? new Date(k.signed_at).toLocaleDateString() : "—"}</td>
                                  <td className="px-2 py-1">{k.ends_at ? new Date(k.ends_at).toLocaleDateString() : "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : <p className="text-[11px] text-muted-foreground">No contracts</p>}
                      </div>

                      <div className="mt-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Meetings ({cMtgs.length})</p>
                        {cMtgs.length ? (
                          <table className="w-full text-[11px] border border-border">
                            <thead className="bg-muted"><tr><th className="text-left px-2 py-1">Ref</th><th className="text-left px-2 py-1">Type</th><th className="text-left px-2 py-1">When</th><th className="text-left px-2 py-1">Scheduled By</th><th className="text-left px-2 py-1">Notes</th></tr></thead>
                            <tbody>
                              {cMtgs.map((m: any) => (
                                <tr key={m.id} className="border-t border-border">
                                  <td className="px-2 py-1">{m.meeting_id || m.id.slice(0, 8)}</td>
                                  <td className="px-2 py-1">{kindLabelP(m.kind)}</td>
                                  <td className="px-2 py-1">{m.scheduled_at ? new Date(m.scheduled_at).toLocaleString() : "—"}</td>
                                  <td className="px-2 py-1">{m.scheduled_by_name || "—"}</td>
                                  <td className="px-2 py-1 break-words">{m.notes || "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : <p className="text-[11px] text-muted-foreground">No meetings</p>}
                      </div>

                      <div className="mt-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Activity Logs ({cLogs.length})</p>
                        {cLogs.length ? (
                          <table className="w-full text-[11px] border border-border">
                            <thead className="bg-muted"><tr><th className="text-left px-2 py-1">When</th><th className="text-left px-2 py-1">Channel</th><th className="text-left px-2 py-1">Actor</th><th className="text-left px-2 py-1">Summary</th></tr></thead>
                            <tbody>
                              {cLogs.map((l: any) => (
                                <tr key={l.id} className="border-t border-border">
                                  <td className="px-2 py-1">{l.occurred_at ? new Date(l.occurred_at).toLocaleString() : "—"}</td>
                                  <td className="px-2 py-1">{channelLabelP(l.channel)}</td>
                                  <td className="px-2 py-1">{l.actor_name || "—"}</td>
                                  <td className="px-2 py-1 break-words">{l.summary || "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : <p className="text-[11px] text-muted-foreground">No activity recorded</p>}
                      </div>
                    </section>
                  );
                });
              })()}
            </div>

          </>

        )}
      </main>

      {logsOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 print:hidden"
          onClick={() => setLogsOpen(false)}
        >
          <div
            className="bg-white w-full sm:max-w-3xl max-h-[92vh] rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="px-4 py-3 border-b border-border bg-gradient-to-r from-slate-50 to-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-slate-700" />
                <h2 className="text-sm font-bold">Activity Logs</h2>
                <span className="text-[11px] font-mono bg-slate-100 px-2 py-0.5 rounded-full text-slate-600">
                  {(ulQ.data?.logs ?? []).length}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setLogsOpen(false)}
                className="grid size-8 place-items-center rounded-full hover:bg-muted"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </header>
            <div className="overflow-y-auto flex-1">
              {ulQ.isLoading ? (
                <div className="grid place-items-center py-10">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : (ulQ.data?.logs ?? []).length === 0 ? (
                <p className="p-6 text-center text-sm text-muted-foreground">No activity logs yet.</p>
              ) : (
                <ul className="divide-y divide-border">
                  {[...(ulQ.data?.logs ?? [])]
                    .sort((a: any, b: any) => String(b.occurred_at ?? "").localeCompare(String(a.occurred_at ?? "")))
                    .map((l: any) => {
                      const ch = l.channel;
                      const chLabel = ch === "call" ? "Call" : ch === "email" ? "Email" : ch === "visit" ? "Visit" : ch === "whatsapp" ? "WhatsApp" : ch === "other" ? "Other" : (ch ?? "—");
                      const chColor = ch === "call" ? "#0ea5e9" : ch === "email" ? "#a855f7" : ch === "visit" ? "#10b981" : ch === "whatsapp" ? "#16a34a" : "#64748b";
                      return (
                        <li key={l.id} className="px-4 py-3 hover:bg-muted/40">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span
                                  className="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                                  style={{ background: `${chColor}1f`, color: chColor }}
                                >
                                  {chLabel}
                                </span>
                                <span className="text-sm font-semibold truncate">{l.client_name || "—"}</span>
                              </div>
                              {l.summary && (
                                <p className="mt-1 text-xs text-slate-700 line-clamp-3">{l.summary}</p>
                              )}
                              <div className="mt-1 text-[11px] text-muted-foreground">
                                {l.actor_name || "—"}
                              </div>
                            </div>
                            <div className="text-[11px] text-muted-foreground tabular-nums whitespace-nowrap shrink-0">
                              {l.occurred_at ? new Date(l.occurred_at).toLocaleString() : "—"}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}


      {selected && (() => {
        const segTint =
          selected.segment === "eat_drink" ? "#f97316" :
          selected.segment === "happenings" ? "#a855f7" :
          selected.segment === "do" ? "#0ea5e9" : "#0d6efd";
        const segLabel =
          selected.segment === "eat_drink" ? "Eat & Drink" :
          selected.segment === "happenings" ? "Happenings" :
          selected.segment === "do" ? "To Do" : selected.segment ?? "—";
        const stTint =
          selected.status === "active" || selected.status === "won" ? "#16a34a" :
          selected.status === "churned" || selected.status === "lost" ? "#dc2626" :
          selected.status === "pending" ? "#f59e0b" : "#0d6efd";
        const initials = String(selected.name || "?")
          .split(/\s+/).filter(Boolean).slice(0, 2)
          .map((w: string) => w[0]?.toUpperCase()).join("") || "?";
        return (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 sm:p-3 print:hidden"
            onClick={() => setSelected(null)}
          >
            <div
              className="flex w-full sm:max-w-lg md:max-w-2xl max-h-[92vh] sm:max-h-[85vh] flex-col rounded-t-2xl sm:rounded-2xl bg-card shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="relative shrink-0 px-4 pt-4 pb-3"
                style={{
                  background: `linear-gradient(135deg, ${segTint}22 0%, rgba(255,255,255,0.95) 70%)`,
                }}
              >
                <span aria-hidden className="sm:hidden absolute left-1/2 top-1.5 -translate-x-1/2 h-1 w-10 rounded-full bg-foreground/15" />
                <button
                  onClick={() => setSelected(null)}
                  className="absolute right-2 top-2 grid size-10 place-items-center rounded-full hover:bg-black/5"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
                <div className="flex items-center gap-3 pr-12">
                  <div
                    className="grid size-12 shrink-0 place-items-center rounded-2xl text-base font-bold text-white shadow-sm"
                    style={{ background: `linear-gradient(135deg, ${segTint}, ${segTint}cc)` }}
                  >
                    {initials}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate text-base font-bold tracking-tight">{selected.name}</h3>
                    <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                      <span
                        className="rounded-full px-2 py-0.5 text-[10px] font-medium"
                        style={{ background: `${segTint}1f`, color: segTint }}
                      >
                        {segLabel}
                      </span>
                      <span
                        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                        style={{ background: `${stTint}1f`, color: stTint }}
                      >
                        <span className="size-1.5 rounded-full" style={{ background: stTint }} />
                        {selected.status ?? "—"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <dl className="grid grid-cols-1 gap-3 overflow-y-auto p-4 pb-6 text-xs sm:grid-cols-2">
                <Field label="Added By" value={repLabel(selected.created_by)} />
                <Field label="Assigned Rep" value={repLabel(selected.assigned_rep)} />
                <Field label="City" value={cityMap[selected.city_id] ?? "—"} />
                <Field label="Email" value={selected.email ?? "—"} />
                <Field label="Phone" value={selected.phone ?? "—"} />
                <Field label="Website" value={selected.website ?? "—"} />
                <Field label="Contact Person" value={selected.contact_person ?? "—"} />
                <Field label="Address" value={selected.address ?? "—"} />
                <Field
                  label="Created"
                  value={selected.created_at ? new Date(selected.created_at).toLocaleString() : "—"}
                />
                <Field
                  label="Updated"
                  value={selected.updated_at ? new Date(selected.updated_at).toLocaleString() : "—"}
                />
                {selected.notes && (
                  <div className="sm:col-span-2">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Notes</div>
                    <div className="mt-0.5 whitespace-pre-wrap">{selected.notes}</div>
                  </div>
                )}
              </dl>
              <div
                className="shrink-0 border-t border-border bg-card/95 backdrop-blur px-4 py-3 grid grid-cols-1 sm:grid-cols-2 gap-2"
                style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
              >
                <button
                  onClick={() => setSelected(null)}
                  className="order-2 sm:order-1 inline-flex h-12 w-full items-center justify-center rounded-xl border border-border bg-card text-sm font-semibold hover:bg-muted"
                >
                  Close
                </button>
                <button
                  onClick={() =>
                    navigate({ to: "/crm/$clientId", params: { clientId: selected.id } })
                  }
                  className="order-1 sm:order-2 inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-foreground text-background text-sm font-semibold hover:opacity-90"
                >
                  <ExternalLink className="h-4 w-4" />
                  Open Full Profile
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  ) : (
    <Outlet />
  )}
</>
);
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5 truncate font-medium">{value}</div>
    </div>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
  tint = "#0d6efd",
  accent,
  active = false,
  onClick,
}: {
  icon: LucideIcon;
  label: string;
  value: React.ReactNode;
  tint?: string;
  accent?: "yellow" | "red";
  active?: boolean;
  onClick?: () => void;
}) {
  const ringColor = active
    ? tint
    : accent === "red"
      ? "rgba(220,53,69,0.35)"
      : accent === "yellow"
        ? "rgba(245,158,11,0.4)"
        : "rgba(13,17,38,0.06)";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`group relative overflow-hidden rounded-2xl p-2.5 sm:p-4 text-left ring-1 backdrop-blur-xl transition-all active:scale-[0.985] hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 ${active ? "ring-2" : ""}`}
      style={{
        background: `linear-gradient(135deg, ${tint}${active ? "2a" : "14"} 0%, rgba(255,255,255,0.85) 55%, rgba(255,255,255,0.95) 100%)`,
        boxShadow: `inset 0 1px 0 rgba(255,255,255,0.7), 0 1px 2px rgba(13,17,38,0.04), 0 10px 24px -16px ${tint}55`,
        // @ts-ignore
        "--tw-ring-color": ringColor,
      }}
    >

      <span
        aria-hidden
        className="pointer-events-none absolute -top-8 -right-8 size-24 rounded-full blur-2xl opacity-60"
        style={{ background: `radial-gradient(circle, ${tint}55, transparent 70%)` }}
      />
      <span
        aria-hidden
        className="absolute inset-y-1.5 left-0 w-[3px] rounded-r-full"
        style={{ background: `linear-gradient(180deg, ${tint}, ${tint}80)` }}
      />
      <span
        aria-hidden
        className="absolute inset-x-3 top-0 h-px"
        style={{ background: `linear-gradient(90deg, transparent, ${tint}55, transparent)` }}
      />
      <div className="relative flex items-start justify-between gap-2">
        <div
          className="grid size-7 sm:size-9 place-items-center rounded-lg sm:rounded-xl shrink-0 shadow-sm"
          style={{
            background: `linear-gradient(135deg, ${tint}, ${tint}cc)`,
            color: "#fff",
          }}
        >
          <Icon className="size-3.5 sm:size-[18px]" strokeWidth={2.4} />
        </div>
      </div>
      <div className="relative mt-1.5 sm:mt-2 text-[9px] sm:text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground truncate">
        {label}
      </div>
      <div className="relative mt-0.5 text-base sm:text-2xl font-extrabold tracking-tight text-foreground tabular-nums truncate">
        {value}
      </div>
    </button>

  );
}
